/**
 * Claude AI Service
 * Provides AI-powered features throughout the platform
 */

import Anthropic from '@anthropic-ai/sdk';

// Initialize client - API key from environment
const getClient = () => {
  const apiKey = import.meta.env.VITE_ANTHROPIC_API_KEY;
  if (!apiKey) {
    throw new Error('VITE_ANTHROPIC_API_KEY is not configured');
  }
  return new Anthropic({ apiKey, dangerouslyAllowBrowser: true });
};

// ============================================
// TEMPLATE ASSISTANCE
// ============================================

export interface TemplateGenerationRequest {
  documentType: string;
  jurisdiction: string;
  description: string;
  includeVariables?: string[];
}

export interface TemplateGenerationResponse {
  content: string;
  suggestedVariables: Array<{
    name: string;
    type: string;
    description: string;
    required: boolean;
  }>;
  explanation: string;
}

/**
 * Generate a new template from a description
 */
export async function generateTemplate(
  request: TemplateGenerationRequest
): Promise<TemplateGenerationResponse> {
  const client = getClient();

  const systemPrompt = `You are an expert legal document automation specialist. You create templates using a custom DSL with the following syntax:

DOCUMENT "Name"
DESCRIPTION "Description"
VERSION "1.0"

SECTION "Section Name"
  Content with {{variables}} and {{variable | filter}}
END

Available features:
- Variables: {{variable.name}}
- Filters: {{value | uppercase}}, {{date | date: 'long'}}, {{amount | currency}}, {{name | possessive}}, {{count | ordinal}}, {{items | pluralize: 'item'}}
- Conditionals: {{#if condition}}...{{/if}}, {{#unless condition}}...{{/unless}}
- Loops: {{#each items as item}}...{{/each}}
- Nested: {{client.address.street}}

Always follow legal document conventions for the specified jurisdiction.
Use clear variable names like grantor.name, grantee.address, property.legal_description.
Include proper signature blocks and notary sections where appropriate.`;

  const response = await client.messages.create({
    model: 'claude-sonnet-4-20250514',
    max_tokens: 4096,
    system: systemPrompt,
    messages: [
      {
        role: 'user',
        content: `Create a ${request.documentType} template for ${request.jurisdiction}.

Description: ${request.description}

${request.includeVariables?.length ? `Include these variables: ${request.includeVariables.join(', ')}` : ''}

Respond with JSON in this exact format:
{
  "content": "The full DSL template content",
  "suggestedVariables": [
    {"name": "variable.name", "type": "text|date|number|boolean|array", "description": "What this variable is for", "required": true}
  ],
  "explanation": "Brief explanation of the template structure and key sections"
}`
      }
    ]
  });

  const text = response.content[0].type === 'text' ? response.content[0].text : '';
  
  // Parse JSON from response
  const jsonMatch = text.match(/\{[\s\S]*\}/);
  if (!jsonMatch) {
    throw new Error('Failed to parse AI response');
  }
  
  return JSON.parse(jsonMatch[0]);
}

/**
 * Improve an existing template
 */
export async function improveTemplate(
  currentContent: string,
  instructions: string
): Promise<{ improvedContent: string; changes: string[] }> {
  const client = getClient();

  const response = await client.messages.create({
    model: 'claude-sonnet-4-20250514',
    max_tokens: 4096,
    system: `You are an expert legal document automation specialist. Improve templates while maintaining the DSL syntax.`,
    messages: [
      {
        role: 'user',
        content: `Improve this template based on the instructions.

Current template:
\`\`\`
${currentContent}
\`\`\`

Instructions: ${instructions}

Respond with JSON:
{
  "improvedContent": "The improved DSL template",
  "changes": ["List of changes made"]
}`
      }
    ]
  });

  const text = response.content[0].type === 'text' ? response.content[0].text : '';
  const jsonMatch = text.match(/\{[\s\S]*\}/);
  if (!jsonMatch) {
    throw new Error('Failed to parse AI response');
  }
  
  return JSON.parse(jsonMatch[0]);
}

// ============================================
// CLAUSE SUGGESTIONS
// ============================================

export interface ClauseSuggestion {
  name: string;
  content: string;
  category: string;
  explanation: string;
  relevanceScore: number;
}

/**
 * Get clause suggestions based on document context
 */
export async function suggestClauses(
  documentType: string,
  jurisdiction: string,
  existingClauses: string[],
  context?: string
): Promise<ClauseSuggestion[]> {
  const client = getClient();

  const response = await client.messages.create({
    model: 'claude-sonnet-4-20250514',
    max_tokens: 2048,
    system: `You are a legal expert specializing in ${jurisdiction} law. Suggest relevant clauses for legal documents.`,
    messages: [
      {
        role: 'user',
        content: `Suggest clauses for a ${documentType} in ${jurisdiction}.

Already included: ${existingClauses.join(', ') || 'None'}
${context ? `Additional context: ${context}` : ''}

Respond with JSON array:
[
  {
    "name": "Clause Name",
    "content": "The clause text with {{variables}} if needed",
    "category": "warranties|boilerplate|signatures|definitions|provisions",
    "explanation": "Why this clause is important",
    "relevanceScore": 0.95
  }
]

Suggest 3-5 relevant clauses not already included.`
      }
    ]
  });

  const text = response.content[0].type === 'text' ? response.content[0].text : '';
  const jsonMatch = text.match(/\[[\s\S]*\]/);
  if (!jsonMatch) {
    return [];
  }
  
  return JSON.parse(jsonMatch[0]);
}

// ============================================
// DOCUMENT ANALYSIS
// ============================================

export interface DocumentAnalysis {
  summary: string;
  keyTerms: Array<{ term: string; definition: string }>;
  parties: Array<{ role: string; name: string }>;
  obligations: Array<{ party: string; obligation: string }>;
  dates: Array<{ description: string; date: string }>;
  risks: Array<{ risk: string; severity: 'low' | 'medium' | 'high'; recommendation: string }>;
  missingElements: string[];
}

/**
 * Analyze a generated document
 */
export async function analyzeDocument(
  documentContent: string,
  documentType: string
): Promise<DocumentAnalysis> {
  const client = getClient();

  const response = await client.messages.create({
    model: 'claude-sonnet-4-20250514',
    max_tokens: 3000,
    system: `You are a legal document analyst. Provide thorough, accurate analysis of legal documents.`,
    messages: [
      {
        role: 'user',
        content: `Analyze this ${documentType}:

${documentContent}

Respond with JSON:
{
  "summary": "2-3 sentence summary of the document",
  "keyTerms": [{"term": "Term", "definition": "What it means in this context"}],
  "parties": [{"role": "Grantor/Grantee/etc", "name": "Party name from document"}],
  "obligations": [{"party": "Who", "obligation": "What they must do"}],
  "dates": [{"description": "What the date is for", "date": "The date"}],
  "risks": [{"risk": "Potential issue", "severity": "low|medium|high", "recommendation": "How to address"}],
  "missingElements": ["Things that might be missing from this document type"]
}`
      }
    ]
  });

  const text = response.content[0].type === 'text' ? response.content[0].text : '';
  const jsonMatch = text.match(/\{[\s\S]*\}/);
  if (!jsonMatch) {
    throw new Error('Failed to parse AI response');
  }
  
  return JSON.parse(jsonMatch[0]);
}

// ============================================
// VARIABLE EXTRACTION
// ============================================

export interface ExtractedVariables {
  variables: Record<string, any>;
  confidence: number;
  clarifications: string[];
}

/**
 * Extract variables from natural language description
 */
export async function extractVariablesFromText(
  userInput: string,
  templateVariables: Array<{ name: string; type: string; required: boolean }>
): Promise<ExtractedVariables> {
  const client = getClient();

  const variablesList = templateVariables
    .map(v => `- ${v.name} (${v.type}, ${v.required ? 'required' : 'optional'})`)
    .join('\n');

  const response = await client.messages.create({
    model: 'claude-sonnet-4-20250514',
    max_tokens: 2048,
    system: `You extract structured data from natural language for legal documents. Be precise and ask for clarification when information is ambiguous.`,
    messages: [
      {
        role: 'user',
        content: `Extract variable values from this description:

"${userInput}"

Template variables needed:
${variablesList}

Respond with JSON:
{
  "variables": {
    "variable.name": "extracted value"
  },
  "confidence": 0.85,
  "clarifications": ["Questions about unclear information"]
}`
      }
    ]
  });

  const text = response.content[0].type === 'text' ? response.content[0].text : '';
  const jsonMatch = text.match(/\{[\s\S]*\}/);
  if (!jsonMatch) {
    throw new Error('Failed to parse AI response');
  }
  
  return JSON.parse(jsonMatch[0]);
}

// ============================================
// CHAT ASSISTANT
// ============================================

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface ChatContext {
  currentPage?: string;
  documentType?: string;
  clientName?: string;
  matterName?: string;
}

/**
 * AI Chat assistant for the platform
 */
export async function chat(
  messages: ChatMessage[],
  context?: ChatContext
): Promise<string> {
  const client = getClient();

  const contextInfo = context
    ? `Current context:
- Page: ${context.currentPage || 'Unknown'}
- Document Type: ${context.documentType || 'N/A'}
- Client: ${context.clientName || 'N/A'}
- Matter: ${context.matterName || 'N/A'}`
    : '';

  const response = await client.messages.create({
    model: 'claude-sonnet-4-20250514',
    max_tokens: 1024,
    system: `You are a helpful AI assistant for a legal practice management platform called Lawity. You help attorneys and staff with:
- Creating and editing document templates
- Understanding the DSL syntax
- Managing clients and matters
- Generating documents
- E-signature workflows

${contextInfo}

Be concise, professional, and helpful. If you don't know something specific about this platform, say so.`,
    messages: messages.map(m => ({
      role: m.role,
      content: m.content
    }))
  });

  return response.content[0].type === 'text' ? response.content[0].text : '';
}

// ============================================
// SMART SEARCH
// ============================================

export interface SearchResult {
  type: 'template' | 'clause' | 'client' | 'matter' | 'document';
  id: string;
  name: string;
  relevance: number;
  snippet: string;
}

/**
 * AI-powered semantic search across the platform
 */
export async function smartSearch(
  query: string,
  items: Array<{ type: string; id: string; name: string; content: string }>
): Promise<SearchResult[]> {
  const client = getClient();

  // For small datasets, use Claude to rank relevance
  if (items.length === 0) return [];

  const itemsList = items
    .slice(0, 50) // Limit to prevent token overflow
    .map((item, i) => `[${i}] ${item.type}: "${item.name}" - ${item.content.slice(0, 200)}...`)
    .join('\n');

  const response = await client.messages.create({
    model: 'claude-sonnet-4-20250514',
    max_tokens: 1024,
    system: `You are a search assistant. Rank items by relevance to the query.`,
    messages: [
      {
        role: 'user',
        content: `Query: "${query}"

Items:
${itemsList}

Return JSON array of relevant items (indices) with relevance scores (0-1):
[{"index": 0, "relevance": 0.95, "snippet": "Why this is relevant"}]

Only include items with relevance > 0.3. Max 10 results.`
      }
    ]
  });

  const text = response.content[0].type === 'text' ? response.content[0].text : '';
  const jsonMatch = text.match(/\[[\s\S]*\]/);
  if (!jsonMatch) return [];

  const rankings = JSON.parse(jsonMatch[0]) as Array<{ index: number; relevance: number; snippet: string }>;

  return rankings
    .filter(r => r.index < items.length)
    .map(r => ({
      type: items[r.index].type as SearchResult['type'],
      id: items[r.index].id,
      name: items[r.index].name,
      relevance: r.relevance,
      snippet: r.snippet
    }))
    .sort((a, b) => b.relevance - a.relevance);
}

// ============================================
// PROOFREAD & SUGGESTIONS
// ============================================

export interface ProofreadResult {
  issues: Array<{
    type: 'grammar' | 'legal' | 'consistency' | 'clarity';
    location: string;
    issue: string;
    suggestion: string;
    severity: 'info' | 'warning' | 'error';
  }>;
  overallScore: number;
  suggestions: string[];
}

/**
 * Proofread generated document content
 */
export async function proofreadDocument(
  content: string,
  documentType: string,
  jurisdiction: string
): Promise<ProofreadResult> {
  const client = getClient();

  const response = await client.messages.create({
    model: 'claude-sonnet-4-20250514',
    max_tokens: 2048,
    system: `You are a legal proofreader specializing in ${jurisdiction} ${documentType} documents. Check for grammar, legal accuracy, consistency, and clarity.`,
    messages: [
      {
        role: 'user',
        content: `Proofread this ${documentType}:

${content}

Respond with JSON:
{
  "issues": [
    {
      "type": "grammar|legal|consistency|clarity",
      "location": "Section or paragraph reference",
      "issue": "What's wrong",
      "suggestion": "How to fix it",
      "severity": "info|warning|error"
    }
  ],
  "overallScore": 85,
  "suggestions": ["General improvement suggestions"]
}`
      }
    ]
  });

  const text = response.content[0].type === 'text' ? response.content[0].text : '';
  const jsonMatch = text.match(/\{[\s\S]*\}/);
  if (!jsonMatch) {
    return { issues: [], overallScore: 100, suggestions: [] };
  }
  
  return JSON.parse(jsonMatch[0]);
}

// ============================================
// EXPORT
// ============================================

export const claudeAI = {
  generateTemplate,
  improveTemplate,
  suggestClauses,
  analyzeDocument,
  extractVariablesFromText,
  chat,
  smartSearch,
  proofreadDocument,
};

export default claudeAI;
